import { useSelector } from 'react-redux';
import type { ServiceCode } from '../../../../common/enums/serviceCode';
import type { IReduxState } from '../../../configureStore';
import type { IDiagnosticReportGetOne } from '../../interfaces/diagnosticReportGetOne';
import type { IProcess } from '../../interfaces/process';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import type { ISupervisionGetOne } from '../../interfaces/supervisionGetOne';
import type { IItemState } from '../../pages/episodes/Episode';
import { useEpisodeContext } from '../../../utils/hooks/useEpisodeContext';
import { ButtonContainer } from '../common';
import { ConfirmDiagnosticReportButton } from './ConfirmDiagnosticReportButton';
import { ConvertToConsultButton } from './ConvertToConsultButton';
import { EditSupervisionButton } from './EditSupervisionButton';
import { PrintCarePathButton } from './PrintCarePathButton';
import { RemoveSupervisionButton } from './RemoveSupervisionButton';
import { TakeDiagnosticReportButton } from './TakeDiagnosticReportButton';
import { TakeSupervisionButton } from './TakeSupervisionButton';

interface ISupervisionReadButtonsProps {
    acceptDiagnosticReport: () => Promise<void>;
    diagnosticReport: IDiagnosticReportGetOne;
    isLastItem: boolean;
    isPreviousRequest: boolean;
    itemState: IItemState;
    process: IProcess;
    reloadProcess: () => Promise<void>;
    removeSupervision?: () => Promise<void>;
    request: IRequestGetOne;
    serviceCode: ServiceCode;
    supervision: ISupervisionGetOne;
    updateItemState: (itemState: IItemState) => Promise<void>;
}

export function SupervisionReadButtons(props: ISupervisionReadButtonsProps) {
    const { patient, onConvertToConsult } = useEpisodeContext();
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const practitionerId = useSelector((state: IReduxState) => state.authentication.id);

    if (isManager) {
        return null;
    }

    return (
        <ButtonContainer
            alignRight
            buttons={[
                <PrintCarePathButton
                    isLastItem={props.isLastItem}
                    request={props.request}
                />,
                <RemoveSupervisionButton
                    removeSupervision={props.removeSupervision}
                    serviceCode={props.serviceCode}
                    supervision={props.supervision}
                />,
                <TakeSupervisionButton
                    practitionerId={practitionerId}
                    reloadProcess={props.reloadProcess}
                    supervision={props.supervision}
                    task={props.process.task}
                />,
                <TakeDiagnosticReportButton
                    diagnosticReport={props.diagnosticReport}
                    reloadProcess={props.reloadProcess}
                />,
                <ConvertToConsultButton
                    acceptDiagnosticReport={props.acceptDiagnosticReport}
                    diagnosticReport={props.diagnosticReport}
                    isPreviousRequest={props.isPreviousRequest}
                    onConvertToConsult={onConvertToConsult}
                    patient={patient}
                    request={props.request}
                    serviceCode={props.request.service.code}
                    serviceId={props.request.serviceId}
                    serviceType={props.request.serviceType}
                />,
                <ConfirmDiagnosticReportButton
                    acceptDiagnosticReport={props.acceptDiagnosticReport}
                    diagnosticReport={props.diagnosticReport}
                    patient={patient}
                    request={props.request}
                    serviceCode={props.serviceCode}
                />,
                <EditSupervisionButton
                    editSupervision={async () => await props.updateItemState({ ...props.itemState, mode: 'edit' })}
                    practitionerId={practitionerId}
                    supervision={props.supervision}
                    task={props.process.task}
                />,
            ]}
            hideButtons={!props.isLastItem}
        />
    );
}
